import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Upload } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip'
import type { CreateEmployeeRequest } from '@/api/model'
import { AccountStatus } from '@/api/model'
import {
  getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey,
  getGetApiCompaniesCurrentEmployeesQueryKey,
  getGetApiCompaniesCurrentGroupsQueryKey,
  usePostApiCompaniesCurrentEmployees,
} from '@/api/generated/company-management-user/company-management-user'
import {
  getGetApiUsersCurrentNotificationsCountQueryKey,
  getGetApiUsersCurrentNotificationsQueryKey,
} from '@/api/generated/user/user'
import { useQueryClient } from '@tanstack/react-query'
import { notify } from '@/lib/notify'
import { readSheetAsRows } from '@/lib/readSpreadsheet'
import { EmployeeImportPreviewDialog } from '../components/EmployeeImportPreviewDialog'

// Excel column headers — data contract with the template file (Mitarbeiterliste.xlsx),
// NOT translatable UI text. Do not move into i18n, otherwise the import breaks.
const COL_FIRST_NAME  = 'Vorname'
const COL_LAST_NAME   = 'Nachname'
const COL_BIRTH_DATE  = 'Geburtsdatum'
const COL_EMPLOYMENT  = 'Anstellungsdatum'
const COL_POSITION    = 'Position'
const COL_GROUP       = 'Gruppe'
const COL_SUB_COMPANY = 'Tochterunternehmen'
const COL_EMPLOYEE_NO = 'Personalnummer'
const COL_PASSWORD    = 'Passwort'
const REQUIRED_COLS   = [COL_FIRST_NAME, COL_LAST_NAME, COL_EMPLOYEE_NO]

export function EmployeesImportButton() {
  const { t } = useTranslation('employees')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const { mutateAsync: importEmployees, isPending } = usePostApiCompaniesCurrentEmployees()
  // Parsed rows awaiting confirmation; non-null while the preview dialog is open.
  const [pendingEmployees, setPendingEmployees] = useState<CreateEmployeeRequest[] | null>(null)

  const handleClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    let rows
    try {
      rows = await readSheetAsRows(file)
    } catch {
      notify.error(t('toasts.importReadError'))
      return
    }

    if (rows.length === 0) {
      notify.error(t('toasts.importNoData'))
      return
    }

    const headers = Object.keys(rows[0]).map(h => h.toString().trim())
    const missing = REQUIRED_COLS.filter(col => !headers.includes(col))
    if (missing.length > 0) {
      notify.error(t('toasts.importMissingCols', { cols: missing.join(', ') }))
      return
    }

    const employeeNos = new Set<string>()
    const employees: CreateEmployeeRequest[] = []
    for (const row of rows) {
      const no = String(row[COL_EMPLOYEE_NO] ?? '').trim()
      if (!no) { notify.error(t('toasts.importNoNumber')); return }
      if (employeeNos.has(no)) { notify.error(t('toasts.importDuplicateNumber', { no })); return }
      employeeNos.add(no)

      employees.push({
        firstName:      String(row[COL_FIRST_NAME] ?? '').trim() || null,
        lastName:       String(row[COL_LAST_NAME] ?? '').trim() || null,
        employeeNumber: no,
        position:       row[COL_POSITION]    ? String(row[COL_POSITION]).trim()    : null,
        subCompanyName: row[COL_SUB_COMPANY] ? String(row[COL_SUB_COMPANY]).trim() : null,
        groupNames:     row[COL_GROUP] ? String(row[COL_GROUP]).split(',').map(g => g.trim()) : [],
        birthDate:      row[COL_BIRTH_DATE]  ? dayjs(row[COL_BIRTH_DATE] as string | number | Date).format('YYYY-MM-DD')  : null,
        employmentDate: row[COL_EMPLOYMENT]  ? dayjs(row[COL_EMPLOYMENT] as string | number | Date).format('YYYY-MM-DD')  : null,
        password:       row[COL_PASSWORD]    ? String(row[COL_PASSWORD]).trim() : null,
        status:         AccountStatus.Active,
      })
    }

    // Parsing succeeded — defer the upload until the user confirms the preview.
    setPendingEmployees(employees)
  }

  const handleConfirm = async () => {
    if (!pendingEmployees) return
    // The create runs synchronously in the request and can take a while — acknowledge
    // the click right away. The result notification follows (BON-2049 pattern).
    notify.info(t('toasts.importStarted'), { description: t('toasts.importStartedHint') })
    try {
      await importEmployees({ data: pendingEmployees })
      notify.success(t('toasts.imported', { count: pendingEmployees.length }))
      // The import creates employees and adds missing groups.
      queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentAccountmanagementEmployeesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentGroupsQueryKey() })
      // The backend writes a CreateEmployeesCompleted notification — without this the
      // header badge would only pick it up after the 5 min staleTime.
      queryClient.invalidateQueries({ queryKey: getGetApiUsersCurrentNotificationsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetApiUsersCurrentNotificationsCountQueryKey() })
    } catch (error) {
      notify.apiError(error, t('toasts.importError'))
    } finally {
      setPendingEmployees(null)
    }
  }

  const handleCancel = () => setPendingEmployees(null)

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.csv"
        className="hidden"
        onChange={handleFileChange}
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleClick}>
              <Upload className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('actions.importTooltip')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <EmployeeImportPreviewDialog
        open={pendingEmployees !== null}
        employees={pendingEmployees ?? []}
        isUploading={isPending}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  )
}
